import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import React, {useEffect, useState} from "react";
import {callApi} from "../../actions/base";
import BaseTooltip from "../utils/BaseTooltip";
import {navigate} from "gatsby";
import {HTTP_METHOD} from "../../constants/http";

const useUserPackages = (showOnlyActive = true) => {
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(0);
  const [activeOnly, setActiveOnly] = useState(showOnlyActive);
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handlePageChange = (e, page) => setPage(page);

  const PaginationFooter = () => (
    <>
      {!!pagesCount && (
        <Pagination
          count={pagesCount}
          page={page}
          onChange={handlePageChange}
        />
      )}
    </>
  );

  const toggleActiveOnly = () => setActiveOnly(!activeOnly);

  const handleSuccess = ({data, meta}) => {
    setPackages(data);
    setLoading(false);
    if (page > meta.last_page) {
      setPage(meta.last_page);
    } else {
      setPagesCount(meta.last_page > 1 ? meta.last_page : 0);
      setPage(meta.current_page);
    }
  };

  const handleError = error => {
    setError(error);
    setLoading(false);
  };

  const callGetPackages = () => {
    const payload = {page};
    if (activeOnly) {
      payload["filter[status]"] = "active";
    }

    const uri = "/packages/instances";
    callApi(uri, payload, handleSuccess, handleError, HTTP_METHOD.GET);
  };

  useEffect(() => {
    setLoading(true);

    callGetPackages();
  }, [activeOnly, page]);

  return {
    packages,
    loading,
    error,
    handleRefresh: callGetPackages,
    toggleActiveOnly,
    activeOnly,
    PaginationFooter,
  };
};

const formatDate = dateString => new Date(dateString).toLocaleDateString();

const PackageValidity = ({data}) => {
  const {status, activeFrom, activeTo} = data;

  let message;

  if (status === "waiting_for_payment") {
    message = "Pakiet zostanie aktywowany w momencie zaksięgowania płatności.";
  } else if (activeFrom && activeTo) {
    message = `Ważny do ${formatDate(activeTo)}`;
  }
  return (
    <>
      {message && (
        <>
          <Typography className={"classes.title"} color="textSecondary">
            {message}
          </Typography>
        </>
      )}
    </>
  );
};

const mapStatuses = {
  active: {
    value: "Aktywny",
    description: "Pakiet jest aktywny i można go wykorzystać.",
    color: "",
  },
  waiting_for_payment: {
    value: "Oczekuje na płatność",
    description: "Dokonaj opłaty aby aktywować pakiet.",
    color: "",
  },
  used: {
    value: "Wykorzystano",
    description: "Wykorzystano wszystkie zlecenia weryfikacyjne.",
    color: "",
  },
  expired: {
    value: "Ważność wygasła",
    description: "Pakiet nie jest już aktywny.",
    color: "",
  },
  cancelled: {
    value: "Anulowano",
    description: "Anulowano pakiet.",
    color: "",
  },
  payment_expired: {
    value: "Nie zrealizowano płatności",
    description: "Płatność nie została zrealizowana w odpowiednim terminie.",
    color: "",
  },
};

const PackageCard = ({data, handleRefresh}) => {
  const {
    status,
    itemsUsedCount,
    itemsTotalCount,
    paymentUrl,
  } = data;

  const showPaymentButton = status === "waiting_for_payment";

  const handleCancel = () => {
    const uri = `/packages/instances/${data.code}`;
    callApi(uri, null, handleRefresh, null, HTTP_METHOD.PUT);
  };

  return (
    <>
      <Card variant="outlined">
        <CardActionArea>
          <CardContent>
            <Box>
              <Box mt={2}>
                <BaseTooltip
                  placement="top-start"
                  title={mapStatuses[status].description}
                  aria-label={mapStatuses[status].description}
                >
                  <Chip
                    style={{cursor: "pointer"}}
                    label={mapStatuses[status].value}
                    variant="outlined"
                  />
                </BaseTooltip>
              </Box>
              {showPaymentButton && (
                <Box mt={2}>
                  <Grid spacing={2} container>
                    <Grid item>
                      <a
                        href={paymentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                        >
                          Przejdź do płatności
                        </Button>
                      </a>
                    </Grid>
                    <Grid item>
                      <Button onClick={handleCancel} variant="outlined">
                        Anuluj
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Box mt={2}>
                <Box>
                  <Typography color="textSecondary">
                    Wykorzystano {`${itemsUsedCount}/${itemsTotalCount}`}
                  </Typography>
                </Box>
                <Box width="100%" mt={1}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.round((itemsUsedCount / itemsTotalCount) * 100)}
                  />
                </Box>
              </Box>
              <Box mt={2}>
                <PackageValidity data={data}/>
              </Box>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};

const UserPackages = () => {
  const {
    packages: userPackages,
    loading,
    error,
    handleRefresh,
    toggleActiveOnly,
    activeOnly,
    PaginationFooter,
  } = useUserPackages();

  return (
    <Box px={2}>
      <Box pb={2} textAlign="center">
        <Typography variant="h5" paragraph>
          Twoje pakiety
        </Typography>
      </Box>
      <Grid container>
        <Grid item>
          <Box mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={activeOnly}
                  onChange={toggleActiveOnly}
                />
              }
              label="Tylko aktywne"
              labelPlacement="end"
            />
          </Box>
        </Grid>
        {loading ? (
          <CircularProgress/>
        ) : (
          <Grid container item spacing={2} style={{maxWidth: "100%"}}>
            {userPackages.length ? (
              userPackages.map(p => (
                <Grid key={p.code} item xs={12}>
                  <PackageCard data={p} handleRefresh={handleRefresh}/>
                </Grid>
              ))
            ) : (
              <Grid container item xs={12} justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={() => navigate("/dashboard/packages/available")}
                >
                  Sprawdź dostępne pakiety
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        <Grid container item justifyContent="center">
          <Grid item>
            <Box my={2}>
              <PaginationFooter/>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserPackages;
